import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { cognitoDeleteUser, cognitoSignIn } from '../login/cognito';
import { useDeleteAuthAccountMutation } from '../../store/backendApi';

const COGNITO_CLIENT_ID = process.env.REACT_APP_CLIENT_ID!;
const COGNITO_REGION = 'eu-central-1';

export interface DeleteAccountModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentEmail: string;
  onAccountDeleted: () => void;
}

export default function DeleteAccountModal(props: DeleteAccountModalProps) {
  const [step, setStep] = useState<'warning' | 'confirm'>('warning');
  const [
    deleteAccountData,
    { data: deleteDataResponse, error: deleteDataError, isLoading: isDeleteDataLoading },
  ] = useDeleteAuthAccountMutation();
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleConfirmDelete = async () => {
    if (!password.trim()) {
      setError('Please enter your password to confirm account deletion');
      return;
    }

    if (confirmationText !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // First authenticate to get access token
      const authResult = await cognitoSignIn(
        COGNITO_REGION,
        COGNITO_CLIENT_ID,
        props.currentEmail,
        password,
      );

      if (authResult.success && authResult.data.AccessToken) {
        // First delete user data from backend
        try {
          await deleteAccountData().unwrap();
        } catch (backendError) {
          console.error('Backend deletion failed:', backendError);
          setError('Failed to delete account data. Please try again.');
          setIsProcessing(false);
          return;
        }

        // Then delete the user account from Cognito
        const deleteResult = await cognitoDeleteUser(COGNITO_REGION, authResult.data.AccessToken);

        if (deleteResult.success) {
          props.onAccountDeleted();
          props.setOpen(false);
        } else {
          if (deleteResult.error === 'InvalidCredentials') {
            setError('Invalid password. Please try again.');
          } else {
            setError('Failed to delete account. Please try again.');
          }
        }
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (error) {
      console.error('Account deletion error:', error);
      setError('An unexpected error occurred. Please try again.');
    }

    setIsProcessing(false);
  };

  const handleClose = () => {
    setStep('warning');
    setPassword('');
    setConfirmationText('');
    setError('');
    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={handleClose} className="relative z-10">
      {/* DialogBackdrop */}
      <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true"></div>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left
            shadow-xl sm:my-8 sm:w-full sm:max-w-md sm:p-6"
          >
            <div>
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
                <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                  {step === 'warning' ? 'Delete Account' : 'Confirm Account Deletion'}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {step === 'warning'
                      ? 'Are you sure you want to delete your account? This action cannot be ' +
                        'undone and will permanently remove all your data, trips, and stories.'
                      : 'This action is irreversible. Please enter your password and type "DELETE" to confirm.'}
                  </p>
                </div>
              </div>
            </div>

            {step === 'warning' && (
              <div className="mt-5 sm:mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-900
                  hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                  focus-visible:outline-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStep('confirm')}
                  className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white
                  shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2
                  focus-visible:outline-offset-2 focus-visible:outline-red-600"
                >
                  Delete Account
                </button>
              </div>
            )}

            {step === 'confirm' && (
              <div className="mt-4">
                <div className="mb-4">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:text-sm/6"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="confirmation" className="block text-sm font-medium text-gray-900">
                    Type "DELETE" to confirm
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmation"
                      name="confirmation"
                      type="text"
                      required
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      disabled={isProcessing}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:text-sm/6"
                      placeholder="Type DELETE"
                    />
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                <div className="mt-5 sm:mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep('warning')}
                    disabled={isProcessing || isDeleteDataLoading}
                    className="flex-1 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-900
                    hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={
                      isProcessing ||
                      isDeleteDataLoading ||
                      password.length === 0 ||
                      confirmationText !== 'DELETE'
                    }
                    className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white
                    shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2
                    focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:bg-gray-300
                    disabled:cursor-not-allowed"
                  >
                    {isProcessing || isDeleteDataLoading ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
